import React, { useState } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Calendar from '../../components/common/Calendar';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useAuth } from '../../contexts/AuthContext';

const CompetitionsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const CalendarSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CalendarInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 1rem;
  line-height: 1.6;
`;

const CompetitionsList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CompetitionsListTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const CompetitionCard = styled.div<{ isApplied?: boolean }>`
  padding: 1.5rem;
  border: 2px solid ${props => props.isApplied ? '#d1e7dd' : '#dee2e6'};
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: ${props => props.isApplied ? '#f8fff9' : 'white'};
  
  &:hover {
    border-color: #000000;
    background: ${props => props.isApplied ? '#f0fff5' : '#f8f9fa'};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const CompetitionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;

const CompetitionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  flex: 1;
`;

const CompetitionDate = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
`;

const CompetitionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const CompetitionDetail = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
`;

const AppliedBadge = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem;
  background: #d1e7dd;
  border-radius: 8px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f5132;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ViewContent = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
`;

const ConsentSection = styled.div`
  margin: 1.5rem 0;
`;

const ConsentLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
`;

const ConsentBox = styled.div`
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  max-height: 120px;
  overflow-y: auto;
  background: #f8f9fa;
  margin-bottom: 0.75rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e9ecef;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #6c757d;
  }
`;

const ConsentText = styled.div`
  font-size: 0.8125rem;
  color: #495057;
  line-height: 1.6;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9375rem;
  color: #343a40;
  cursor: pointer;
  line-height: 1.4;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface Competition {
  id: number;
  date: string;
  time: string;
  title: string;
  location: string;
  description: string;
  isApplied?: boolean;
}

const MemberCompetitions: React.FC = () => {
  const { user } = useAuth();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const [ssn, setSsn] = useState('');
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  // TODO: 백엔드 API에서 대회 일정 목록 가져오기
  // GET /api/competitions?year=2025&month=1
  const [competitions, setCompetitions] = useState<Competition[]>([
    { 
      id: 1, 
      date: '2025-01-20', 
      time: '14:00', 
      title: '신년 친선 대회',
      location: '수원체육관', 
      description: '새해를 맞아 친선 대회를 개최합니다.',
      isApplied: false
    },
    { 
      id: 2, 
      date: '2025-02-15', 
      time: '13:00', 
      title: '겨울 리그전',
      location: '수원실내체육관', 
      description: '겨울 시즌 리그전입니다.',
      isApplied: true
    },
  ]);

  const competitionDates = competitions.map(c => c.date);

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    // TODO: 새로운 월의 대회 일정 불러오기
  };

  const handleCompetitionClick = (competition: Competition) => {
    setSelectedCompetition(competition);
    setSsn('');
    setPrivacyAgreed(false);
    setIsModalOpen(true);
  };

  const formatSSN = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    const limited = numbers.slice(0, 13);
    
    if (limited.length <= 6) {
      return limited;
    } else {
      return `${limited.slice(0, 6)}-${limited.slice(6)}`;
    }
  };

  const handleSSNChange = (value: string) => {
    setSsn(formatSSN(value));
  };

  const handleApply = () => {
    if (!selectedCompetition) return;

    if (!ssn || ssn.length < 14) {
      alert('주민등록번호 13자리를 입력해주세요.');
      return;
    }

    if (!privacyAgreed) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    // TODO: 대회 참가 신청 API
    // POST /api/competitions/{id}/apply
    // 요청: { ssn }
    setCompetitions(competitions.map(c =>
      c.id === selectedCompetition.id
        ? { ...c, isApplied: true }
        : c
    ));

    setIsModalOpen(false);
    alert('대회 참가 신청이 완료되었습니다!');
  };

  const handleCancelApply = () => {
    if (!selectedCompetition) return;

    if (window.confirm('참가 신청을 취소하시겠습니까?')) {
      // TODO: 대회 참가 취소 API
      // DELETE /api/competitions/{id}/apply
      setCompetitions(competitions.map(c =>
        c.id === selectedCompetition.id
          ? { ...c, isApplied: false }
          : c
      ));

      setIsModalOpen(false);
      alert('참가 신청이 취소되었습니다.');
    }
  };

  return (
    <DashboardLayout title="대회 일정">
      <CompetitionsContainer>
        <Title>대회 일정</Title>
        
        <CalendarSection>
          <Calendar
            year={currentYear}
            month={currentMonth}
            scheduledDates={competitionDates}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
            readOnly={true}
          />
          <CalendarInfo>
            ✓ 검은색 테두리 날짜는 대회가 있는 날입니다. 클릭하여 상세 정보를 확인할 수 있습니다.
          </CalendarInfo>
        </CalendarSection>
        
        <CompetitionsList>
          <CompetitionsListTitle>대회 목록</CompetitionsListTitle>
          {competitions.length > 0 ? (
            competitions
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(competition => (
                <CompetitionCard 
                  key={competition.id} 
                  isApplied={competition.isApplied}
                  onClick={() => handleCompetitionClick(competition)}
                >
                  <CompetitionHeader>
                    <CompetitionTitle>🏆 {competition.title}</CompetitionTitle>
                    <CompetitionDate>📅 {competition.date}</CompetitionDate>
                  </CompetitionHeader>
                  <CompetitionDetails>
                    <CompetitionDetail>🕐 {competition.time}</CompetitionDetail>
                    <CompetitionDetail>📍 {competition.location}</CompetitionDetail>
                    <CompetitionDetail>📝 {competition.description}</CompetitionDetail>
                  </CompetitionDetails>
                  {competition.isApplied && (
                    <AppliedBadge>✓ 참가 신청 완료</AppliedBadge>
                  )}
                </CompetitionCard>
              ))
          ) : (
            <EmptyState>등록된 대회가 없습니다.</EmptyState>
          )}
        </CompetitionsList>
        
        {/* 대회 상세 및 참가 신청 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>대회 상세 정보</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            {selectedCompetition && (
              <>
                <ViewContent>
                  <DetailRow>
                    <DetailLabel>대회명</DetailLabel>
                    <DetailValue>{selectedCompetition.title}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>날짜</DetailLabel>
                    <DetailValue>{selectedCompetition.date}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>시간</DetailLabel>
                    <DetailValue>{selectedCompetition.time}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>장소</DetailLabel>
                    <DetailValue>{selectedCompetition.location}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>설명</DetailLabel>
                    <DetailValue>{selectedCompetition.description}</DetailValue>
                  </DetailRow>
                </ViewContent>
                
                {!selectedCompetition.isApplied ? (
                  <>
                    <Input
                      label="주민등록번호"
                      type="text"
                      value={ssn}
                      onChange={(e) => handleSSNChange(e.target.value)}
                      placeholder="000000-0000000"
                      required
                    />
                    
                    <ConsentSection>
                      <ConsentLabel>개인정보 수집 및 이용 동의</ConsentLabel>
                      <ConsentBox>
                        <ConsentText>
                          <strong>수집 목적:</strong> 대회 참가자 명단 작성 및 보험 가입
                          <br /><br />
                          <strong>수집 항목:</strong> 이름, 주민등록번호
                          <br /><br />
                          <strong>보유 기간:</strong> 대회 종료 후 3개월
                          <br /><br />
                          귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있으며, 
                          동의하지 않을 경우 대회 참가가 제한될 수 있습니다.
                        </ConsentText>
                      </ConsentBox>
                      
                      <CheckboxWrapper>
                        <Checkbox
                          type="checkbox"
                          id="privacy"
                          checked={privacyAgreed}
                          onChange={(e) => setPrivacyAgreed(e.target.checked)}
                        />
                        <CheckboxLabel htmlFor="privacy">
                          개인정보 수집 및 이용에 동의합니다 (필수)
                        </CheckboxLabel>
                      </CheckboxWrapper>
                    </ConsentSection>
                    
                    <ModalActions>
                      <Button
                        variant="outline"
                        size="large"
                        fullWidth
                        onClick={() => setIsModalOpen(false)}
                      >
                        닫기
                      </Button>
                      <Button
                        variant="primary"
                        size="large"
                        fullWidth
                        onClick={handleApply}
                      >
                        참가 신청
                      </Button>
                    </ModalActions>
                  </>
                ) : (
                  <ModalActions>
                    <Button
                      variant="outline"
                      size="large"
                      fullWidth
                      onClick={() => setIsModalOpen(false)}
                    >
                      닫기
                    </Button>
                    <Button
                      variant="danger"
                      size="large"
                      fullWidth
                      onClick={handleCancelApply}
                    >
                      참가 취소
                    </Button>
                  </ModalActions>
                )}
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      </CompetitionsContainer>
    </DashboardLayout>
  );
};

export default MemberCompetitions;

